/**
 * `/settings/blocked`, the fan's blocked accounts, with a per-row unblock.
 * The file is named `settings_.blocked` so this route stays out of
 * `/settings`'s tree (no Outlet). The row leaves the list through the
 * generated hook's own invalidation, never a manual refetch.
 */
import { useBlockList, useBlockRemove } from "@sportsmet/profile-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../errors/apiErrorBus";
import { SettingsBackLink } from "../features/account/SettingsBackLink";
import { BlockedList } from "../features/privacy/BlockedList";
import {
  BLOCKED_CONTEXT,
  BLOCKED_HEADING,
  BLOCKED_LIST_PAGE_LIMIT,
  BLOCKED_TRUNCATED_NOTICE,
  toBlockedRow,
  type typeBlockedRow,
  UNBLOCK_CONFIRM_BODY,
  UNBLOCK_LABEL,
  unblockConfirmTitle,
} from "../features/privacy/privacyScreenLogic";
import { RoutePendingScreen } from "../shell/RoutePendingScreen";
import { SectionScreen } from "../shell/SectionScreen";

const BlockedScreen = (): ReactNode => {
  const list = useBlockList({ limit: BLOCKED_LIST_PAGE_LIMIT });
  const removeBlock = useBlockRemove();

  useEffect(() => {
    const code = extractErrorCode(list.data);
    if (code !== null) showApiError(list.data, undefined, list.dataUpdatedAt);
  }, [list.dataUpdatedAt]);

  if (list.isPending) return <RoutePendingScreen />;

  const rows: typeBlockedRow[] = (list.data?.data?.items ?? []).map(
    toBlockedRow,
  );
  const isRosterComplete = (list.data?.data?.nextCursor ?? null) === null;
  const unblockingAccountId =
    removeBlock.isPending && removeBlock.variables.blockedAccountId
      ? removeBlock.variables.blockedAccountId
      : null;

  async function handleUnblock(row: typeBlockedRow): Promise<void> {
    const confirmed = await confirmDestructive({
      title: unblockConfirmTitle(row.displayName),
      body: UNBLOCK_CONFIRM_BODY,
      confirmLabel: UNBLOCK_LABEL,
    });
    if (!confirmed) return;
    try {
      showApiError(
        await removeBlock.mutateAsync({ blockedAccountId: row.accountId }),
      );
    } catch {
      showOfflineError();
    }
  }

  return (
    <SectionScreen heading={BLOCKED_HEADING} back={<SettingsBackLink />}>
      <BlockedList
        rows={rows}
        unblockingAccountId={unblockingAccountId}
        onUnblock={(row) => {
          void handleUnblock(row);
        }}
      />
      {isRosterComplete ? null : (
        <p
          data-testid="blocked-truncated"
          className="text-caption text-ink-secondary"
        >
          {BLOCKED_TRUNCATED_NOTICE}
        </p>
      )}
      <p
        data-testid="blocked-context"
        className="text-caption text-ink-secondary"
      >
        {BLOCKED_CONTEXT}
      </p>
    </SectionScreen>
  );
};

export const Route = createFileRoute("/settings_/blocked")({
  beforeLoad: requireSignedIn,
  component: BlockedScreen,
});
